/**
 * Shared authenticated HTTP client.
 *
 * The backend prefers the Bearer header over the ni_access cookie, and the
 * access token expires after ~30 min. Any service that sent a stale Bearer
 * 401'd (dashboard, notifications, patients...). authedFetch refreshes the
 * token once via the 7-day ni_refresh cookie and retries, so a mid-session
 * expiry stops breaking the app. Use this everywhere instead of raw fetch.
 */
import { API_BASE_URL } from '../config/api';
import authService from './authService';
import { isGuest, blockGuestWrite, guestBlockedError } from './guestMode';

let _refreshInFlight = null;

export async function refreshAccessToken() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: '{}',
      });
      if (!r.ok) return false;
      const d = await r.json().catch(() => ({}));
      if (d.access_token) {
        authService.setTokens(d.access_token, d.refresh_token || authService.getRefreshToken());
        return true;
      }
      return false;
    } catch (_) {
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

export async function authedFetch(path, { method = 'GET', body, headers = {} } = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  // Guest mode: writes prompt for login instead of hitting the API.
  if (!['GET', 'HEAD'].includes(method.toUpperCase()) && !url.includes('/auth/') && blockGuestWrite()) {
    throw guestBlockedError();
  }
  const doFetch = () => {
    const token = authService.getAccessToken();
    const h = { ...headers };
    if (body !== undefined) h['Content-Type'] = 'application/json';
    if (token) h.Authorization = `Bearer ${token}`;
    return fetch(url, {
      method,
      headers: h,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };
  let res = await doFetch();
  if (res.status === 401 && !isGuest() && (await refreshAccessToken())) {
    res = await doFetch();
  }
  return res;
}

// Turn this backend's error bodies into a human message. Validation errors
// come as {error:{message, details:[{field,message}]}}; HTTPExceptions as
// {detail}. Falls back to the HTTP status so the UI never shows a bare code.
export function extractError(body, status) {
  if (body) {
    if (typeof body.detail === 'string') return body.detail;
    if (body.error) {
      const e = body.error;
      if (Array.isArray(e.details) && e.details.length) {
        return e.details
          .map((d) => `${d.field ? d.field.replace('body.', '') + ': ' : ''}${d.message}`)
          .join('; ');
      }
      if (e.message) return e.message;
    }
    if (typeof body.message === 'string') return body.message;
  }
  return `HTTP ${status}`;
}
